import { Box } from '@mui/material';
import { useParams } from 'react-router';

import {
  ContractFactoryItem,
  FieldStaffItem,
  PropertyFieldValue,
  PropertyRegistrationForm,
  ReceiptDocumentItem,
} from '../../types/property.types';

import { BasicInformationSection } from './basic-information-section';
import { OnsiteStaffSection } from './onsite-staff-section';
import { PropertyRepresentativesSection } from './property-representatives-section';
import { ContractFactorySection } from './contract-factory.section';
import { RegistrationReceivedSection } from './registration-received-section';

import { Department, Staff } from '@/features/companies/types';

interface BasicSectionCommonProps {
  addFieldStaff: () => void;
  updateFieldStaff: (
    indexOrId: string | number,
    field: keyof FieldStaffItem,
    value: string | number
  ) => void;
  removeFieldStaff: (indexOrId: string | number) => void;
  addContractFactory: () => void;
  updateContractFactory: (
    indexOrId: string | number,
    field: keyof ContractFactoryItem,
    value: string | number
  ) => void;
  removeContractFactory: (indexOrId: string | number) => void;
  handleDepartmentChange: (departmentId: string) => void;
  formData: PropertyRegistrationForm;
  originalFormData?: PropertyRegistrationForm;
  updateField: (field: keyof PropertyRegistrationForm, value: PropertyFieldValue) => void;
  getFieldError: (field: string) => string;
  isFieldValid: (field: string) => boolean;
  departments: Department[];
  departmentsLoading: boolean;
  salesStaffs: Staff[];
  deliveryStaffs: Staff[];
  staffsLoading: boolean;
  customers: unknown[];
  constructionCompanies: unknown[];
  companiesLoading: boolean;
  salesStaffSelected?: Staff | null;
  deliveryStaffSelected?: Staff | null;
  departmentSelected?: Department | null;
  canManagePrice: boolean;
  onDocumentsUploaded?: () => void;
  initialReceiptDocuments?: ReceiptDocumentItem[];
  handleFetchInitialReceiptDocuments?: () => Promise<void>;
}

const BasicSectionCommon = ({
  formData,
  originalFormData,
  updateField,
  getFieldError,
  isFieldValid,
  addFieldStaff,
  updateFieldStaff,
  removeFieldStaff,
  addContractFactory,
  updateContractFactory,
  removeContractFactory,
  handleDepartmentChange,
  departments,
  departmentsLoading,
  salesStaffs,
  deliveryStaffs,
  staffsLoading,
  customers,
  constructionCompanies,
  companiesLoading,
  salesStaffSelected,
  deliveryStaffSelected,
  departmentSelected,
  canManagePrice,
  onDocumentsUploaded,
  initialReceiptDocuments,
  handleFetchInitialReceiptDocuments,
}: BasicSectionCommonProps) => {
  const HEADER_OFFSET = 50;
  const { propertyId } = useParams<{ propertyId: string }>();

  return (
    <>
      {/* Basic Information Section */}
      <Box id='basic-information-section' sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px` }}>
        <BasicInformationSection
          formData={formData}
          onUpdateField={updateField}
          getFieldError={getFieldError}
          isFieldValid={isFieldValid}
          customers={customers}
          constructionCompanies={constructionCompanies}
          companiesLoading={companiesLoading}
          originalFormData={originalFormData}
        />
      </Box>

      {/* Onsite Staff Section */}
      <Box id='onsite-staff-section' sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px` }}>
        <OnsiteStaffSection
          formData={formData}
          onUpdateField={updateField}
          onAddFieldStaff={addFieldStaff}
          onUpdateFieldStaff={updateFieldStaff}
          onRemoveFieldStaff={removeFieldStaff}
          getFieldError={getFieldError}
          isFieldValid={isFieldValid}
          originalFormData={originalFormData}
        />
      </Box>

      {/* Representatives Section */}
      <Box id='representatives-section' sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px` }}>
        <PropertyRepresentativesSection
          formData={formData}
          onUpdateField={updateField}
          getFieldError={getFieldError}
          isFieldValid={isFieldValid}
          departments={departments}
          departmentsLoading={departmentsLoading}
          departmentSelected={departmentSelected}
          salesStaffs={salesStaffs}
          salesStaffSelected={salesStaffSelected}
          deliveryStaffs={deliveryStaffs}
          deliveryStaffSelected={deliveryStaffSelected}
          staffsLoading={staffsLoading}
          onDepartmentChange={handleDepartmentChange}
          originalFormData={originalFormData}
        />
      </Box>

      {/* Contract Factory Section */}
      <Box id='contract-factory-section' sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px` }}>
        <ContractFactorySection
          formData={formData}
          originalFormData={originalFormData}
          onUpdateField={updateField}
          onAddContractFactory={addContractFactory}
          onUpdateContractFactory={updateContractFactory}
          onRemoveContractFactory={removeContractFactory}
          getFieldError={getFieldError}
          isFieldValid={isFieldValid}
          canManagePrice={canManagePrice}
        />
      </Box>
      {propertyId && (
        <>
          {/* Registration Received Section */}
          <Box
            id='registration-received-section'
            sx={{ scrollMarginTop: `${HEADER_OFFSET + 12}px` }}
          >
            <RegistrationReceivedSection
              initialReceiptDocuments={initialReceiptDocuments || []}
              handleFetchInitialReceiptDocuments={
                handleFetchInitialReceiptDocuments || (async () => {})
              }
              onDocumentsUploaded={onDocumentsUploaded}
            />
          </Box>
        </>
      )}
    </>
  );
};

export default BasicSectionCommon;
